import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import AnimatedMapSprite from "../../Pages/Maps/logic/AnimimatedMapSprite";

// Animated box component that cycles through different box types
const AnimatedBox = () => {
  const boxOptions = ["shadow", "golden", "diamond", "emerald", "ruby", "sapphire"];
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const intervalRef = useRef(null);
  
  useEffect(() => {
    // Start the animation interval
    intervalRef.current = setInterval(() => {
      setCurrentBoxIndex(prevIndex => (prevIndex + 1) % boxOptions.length);
    }, 1500); // Change box every 1.5 seconds
    
    // Clean up interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [boxOptions.length]);
  
  return (
    <img
      src={`/items/${boxOptions[currentBoxIndex]}box.png`}
      alt={`${boxOptions[currentBoxIndex]} box`}
      width="40"
      height="40"
      style={{ imageRendering: 'pixelated' }}
    />
  );
};

const CollectionContestModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [collectionDetails, setCollectionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);
  const [submitting, setSubmitting] = useState(false);

  // Rewards configuration
  const rewards = [
    {
      place: "1st",
      coins: "75,000",
      ultraLures: 5,
      safe: "golden",
    },
    {
      place: "2nd",
      coins: "50,000",
      ultraLures: 2,
      safe: "safe",
    },
    {
      place: "3rd",
      coins: "25,000",
      ultraLures: 1,
      safe: "bronze",
    },
  ];

  // Fetch collection contest details when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCollectionDetails();
      
      // Update current time every second for countdown
      const timer = setInterval(() => {
        setCurrentTime(Date.now() / 1000);
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isOpen]);

  const fetchCollectionDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/collection/details", { withCredentials: true });
      setCollectionDetails(res.data);
    } catch (err) {
      console.error("Failed to fetch collection details:", err);
    } finally {
      setLoading(false);
    }
  };

  const getTimeLeft = (endEpoch) => {
    if (!endEpoch) return "Unknown";
    
    const diff = Math.max(0, Math.floor(endEpoch - currentTime));
    const d = Math.floor(diff / (3600 * 24));
    const h = Math.floor((diff % (3600 * 24)) / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return diff > 0 ? `${d}d ${h}h ${m}m ${s}s` : "Expired";
  };

  // Handle Submit All button click
  const handleSubmitAll = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const res = await api.post("/v1/collection/submit-all", {}, { withCredentials: true });
      const response = res.data;
      
      // Update "Your Submitted" count
      setCollectionDetails((prev) => ({
        ...prev,
        currentUserCollection: prev.currentUserCollection + response.submittedCount,
      }));
      
      // Close modal immediately
      toggle();
      
      // Show floating text for successful submission
      if (response.submittedCount > 0) {
        spawnText(
          `Submitted ${response.submittedCount} ${collectionDetails.currentContestPokemon.name}!`, 
          playerPos.x, 
          playerPos.y - 20, 
          "#4CAF50"
        );
        
        // Show reward message
        setTimeout(() => {
          spawnText(
            `Earned ${response.reward.coins} coins and ${response.reward.money} money!`, 
            playerPos.x, 
            playerPos.y - 40, 
            "#FFD700"
          );
        }, 1000);
      } else {
        spawnText("No Pokémon to submit!", playerPos.x, playerPos.y - 20, "#f44336");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to submit Pokémon.";
      spawnText(errorMessage, playerPos.x, playerPos.y - 20, "#f44336");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="collection-contest-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '60px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
          
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>Collection Contest</h3>
          </div>
          
          <button
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '10px' }}>Loading collection contest details...</p>
            </div>
          ) : (
            <>
              {/* Info Section */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h4 style={{ 
                  fontSize: '18px', 
                  marginBottom: '12px',
                  color: '#ffe066',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  Collection Contest
                </h4>
                <p style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  <strong>Note:</strong> The new round starts at the beginning of each month.
                </p>
                <p style={{ fontSize: '14px', lineHeight: '1.5', display: 'flex', alignItems: 'center' }}>
                  For every Pokémon you submit, you earn{" "}
                  <img
                    src="/items/coin.png"
                    alt="coin"
                    width="24"
                    style={{ verticalAlign: "middle", margin: "0 5px" }}
                  />
                  5 and{" "}
                  <img
                    src="/items/money.png"
                    alt="money"
                    width="24"
                    style={{ verticalAlign: "middle", margin: "0 5px" }}
                  />
                  5,000 - 7,000.
                </p>
              </div>

              {/* Current Contest Section */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h5 style={{ 
                  fontSize: '16px', 
                  marginBottom: '16px',
                  color: '#ffe066'
                }}>
                  Current Contest
                </h5>
                
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  justifyContent: 'space-around',
                  gap: '16px'
                }}>
                  {/* Contest Pokémon */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '150px',
                    flex: '1 1 200px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {collectionDetails?.currentContestPokemon && (
                      <>
                        <img
                          src={collectionDetails.currentContestPokemon.pic}
                          alt={collectionDetails.currentContestPokemon.name}
                          width="100"
                          height="100"
                          style={{ 
                            marginBottom: '8px',
                            imageRendering: 'pixelated'
                          }}
                        />
                        <div style={{ fontWeight: 'bold' }}>
                          {collectionDetails.currentContestPokemon.name}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Rotating Map Image */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '150px',
                    flex: '1 1 200px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {collectionDetails?.currentContestPokemon && (
                      <>
                        <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <AnimatedMapSprite
                            pokeName={collectionDetails.currentContestPokemon.name}
                            folder="collection"
                          />
                        </div>
                        <div style={{ marginTop: '8px', fontWeight: 'bold' }}>
                          Map Sprite
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Contest Spawn Rate & Expiration */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '150px',
                    flex: '1 1 200px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ fontSize: '16px', marginBottom: '12px' }}>
                      Spawn Rate: {collectionDetails?.currentContestSpawnRate}%
                    </div>
                    <div style={{ 
                      fontSize: '16px',
                      color: '#ffe066',
                      fontWeight: 'bold'
                    }}>
                      Expires in:
                      <div style={{ marginTop: '8px' }}>
                        {getTimeLeft(collectionDetails?.currentContestExpiration)}
                      </div>
                    </div>
                  </div>
                  
                  {/* User's Submission Count & Submit All Button */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '150px',
                    flex: '1 1 200px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                      Amount Submitted:
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold',
                      color: '#ffe066',
                      marginBottom: '12px'
                    }}>
                      {collectionDetails?.currentUserCollection || 0}
                    </div>
                    <button
                      onClick={handleSubmitAll}
                      disabled={submitting}
                      style={{
                        background: submitting 
                          ? 'rgba(74, 85, 104, 0.5)' 
                          : 'linear-gradient(to bottom, #4a5568, #2d3748)',
                        border: 'none',
                        color: submitting ? '#a0aec0' : '#fff',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => {
                        if (!submitting) {
                          e.target.style.background = 'linear-gradient(to bottom, #5a6978, #3d4758)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!submitting) {
                          e.target.style.background = 'linear-gradient(to bottom, #4a5568, #2d3748)';
                          e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                        }
                      }}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit All"
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Rewards Table */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h5 style={{ 
                  fontSize: '16px', 
                  marginBottom: '16px',
                  color: '#ffe066',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                 Rewards
                </h5>
                
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  padding: '8px'
                }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Place</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Coins</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Color Box</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Ultra Lures</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Safe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewards.map((reward, idx) => (
                        <tr key={idx} style={{ 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}>{reward.place}</td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <img
                              src="/items/coin.png"
                              alt="coin"
                              width="24"
                              style={{ verticalAlign: 'middle' }}
                            />
                            {reward.coins}
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center'
                          }}>
                            <AnimatedBox />
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <img
                              src="/items/ultralure.png"
                              alt="ultralure"
                              width="30"
                              style={{ verticalAlign: 'middle' }}
                            />
                            {reward.ultraLures}
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center'
                          }}>
                            <img
                              src={
                                reward.safe === "golden"
                                  ? "/items/goldensafe.png"
                                  : reward.safe === "safe"
                                    ? "/items/safe.png"
                                    : "/items/bronzesafe.png"
                              }
                              alt={`${reward.safe} safe`}
                              width="40"
                              height="40"
                              style={{ imageRendering: 'pixelated' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Top 5 Collection Leaders */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h5 style={{ 
                  fontSize: '16px', 
                  marginBottom: '16px',
                  color: '#ffe066'
                }}>
                  Top 5 Collection Leaders
                </h5>
                
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  padding: '8px'
                }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Rank</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Avatar</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Username</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Collection Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectionDetails?.topCollectionPlayers?.map((player, idx) => (
                        <tr key={idx} style={{ 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}>
                            {idx + 1}
                            {idx === 0
                              ? "st"
                              : idx === 1
                                ? "nd"
                                : idx === 2
                                  ? "rd"
                                  : "th"}
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center'
                          }}>
                            <img
                              src={`/avatars/${player.avatar}.png`}
                              alt={player.username}
                              width="40"
                              height="40"
                              style={{ borderRadius: '50%' }}
                            />
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center'
                          }}>
                            <a
                              href={`/playerprofile?user=${player.username}`}
                              style={{ 
                                textDecoration: 'none', 
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              {player.username}
                            </a>
                          </td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}>{player.collection}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionContestModal;
